import {coordsToKey} from "../coords/coordsToKey";
import {getNeighborHexKeys} from "../coords/getNeighborHexKeys";
import {getGoblinsTrapDamageRoll} from "../dice/getGoblinsTrapDamageRoll";
import {getTurnOrderFromPlayer} from "../players/getTurnOrderFromPlayer";
import {isUnitFaction} from "../factions/isUnitFaction";
import {FACTIONS} from "../../constants/factions";
import {calculateOrcLoss} from "../factions/orcs/calculateOrcLoss";
import {checkIfUnitTypeIsFlyingForFaction} from "../../configs/factions";

/**
 * Чистая синхронная функция — единственная точка правды для взаимодействия
 * ловушек Гоблинов с перемещением вражеского отряда (см.
 * openspec/changes/add-goblins-faction design.md Decision 4). Проходит по
 * гексам маршрута ПОСЛЕДОВАТЕЛЬНО (начиная со второго — первый гекс пути это
 * исходная позиция юнита, на ней ловушка сработать не может), на каждом шаге
 * ищет ловушки, смежные этому гексу (смежность включает сам гекс) и
 * враждебные движущемуся отряду (trap.ownerId !== movingUnit.ownerId — то же
 * определение "враждебный", что уже использует hasAdjacentEnemyUnit),
 * применяет их суммарный урон и останавливается, если отряд погиб раньше
 * конца маршрута. Вызывается ОДИН РАЗ, до диспатча любых экшенов — и в клике
 * игрока (GameScene.handleMovementPhaseClick), и в пути ботов
 * (decideMovementStep.js), включая мгновенное применение в
 * store.__isBotSandbox.
 *
 * Для Орков реальная потеря численности (remainingSize) считается через
 * calculateOrcLoss от НАКОПЛЕННОГО за всё перемещение урона (не 1:1 и не
 * отдельно на каждый шаг) - та же конвертация и та же гранулярность "весь
 * урон одной боевой фазы разом", что уже используется для урона ближнего боя/
 * стрельбы (см. add-goblins-faction design.md, пункт 8 ручного тестирования).
 * Числа внутри triggeredTrapEvents/trapsByOwner остаются НЕконвертированными
 * (сырой урон конкретной ловушки, нужен для индикатора на её гексе) -
 * округление по правилу брони относится к отряду-жертве в целом, а не к
 * отдельной сработавшей ловушке.
 *
 * @param {{q: number, r: number}[]} path - путь от старта (path[0]) до цели,
 *   как возвращает calculateAvailableMovementCells (paths[hexKey])
 * @param {object} movingUnit - перемещаемый юнит (ownerId, size, type)
 * @param {{id: string, ownerId: string, hexKey: string}[]} traps - все
 *   ловушки на поле (state.goblinsTraps)
 * @param {string[]} turnOrder - порядок хода игроков (state.turnOrder),
 *   для tie-break атрибуции убийства при равном уроне/числе ловушек
 * @returns {{
 *   finalHexKey: string,
 *   finalStepIndex: number,
 *   unitDied: boolean,
 *   remainingSize: number,
 *   killerPlayerId: (string|null),
 *   triggeredTrapEvents: {hexKey: string, trapsByOwner: {ownerId: string, damage: number}[]}[],
 *   removedTrapIds: string[],
 * }}
 *   triggeredTrapEvents сгруппированы по СОБСТВЕННОМУ hexKey ловушки (не по
 *   гексу шага, на котором отряд оказался смежен ей) - индикатор урона на
 *   поле должен показываться там, где стояла ловушка, а не там, где отряд
 *   в неё "въехал" (design.md Decision 22)
 */
export const resolveMovementWithTraps = (path, movingUnit, traps, turnOrder) => {
  const lastIndex = path.length - 1;
  const lastHex = path[lastIndex];

  const noOpResult = {
    finalHexKey: coordsToKey(lastHex.q, lastHex.r),
    finalStepIndex: lastIndex,
    unitDied: false,
    remainingSize: movingUnit.size,
    killerPlayerId: null,
    triggeredTrapEvents: [],
    removedTrapIds: [],
  };

  if (!traps || traps.length === 0 || path.length < 2) {
    return noOpResult;
  }

  // "Летает ли отряд" - свойство пары (тип отряда, фракция), а не одного лишь
  // типа (см. checkIfUnitTypeIsFlyingForFaction, add-goblins-faction
  // design.md): у Нежити в одной фракции есть и наземные, и летающие типы, а
  // у Драконов/Феникса летает именно ИХ D20, а не D20 вообще
  const isFlying = checkIfUnitTypeIsFlyingForFaction(movingUnit.type, movingUnit.factionId);

  // Летающие отряды проверяются только в финальной точке маршрута (см.
  // спека interruptible-unit-movement, требование "Летающие отряды
  // проверяются только в финальной точке")
  const stepIndexesToCheck = isFlying
    ? [lastIndex]
    : path.slice(1).map((_, idx) => idx + 1);

  // Орки теряют численность по своему правилу брони (2 за каждые 3 урона,
  // затем 1 за оставшиеся 2, 0 за оставшийся 1 - calculateOrcLoss), а не 1:1,
  // как остальные фракции - урон от ловушек в этом смысле ничем не отличается
  // от урона ближнего боя/стрельбы (см. calcUnitMeleeDamageWithFortress/
  // APPLY_SHOOTING_ACTIONS, тот же calculateOrcLoss). Конвертация применяется
  // к НАКОПЛЕННОМУ за всё перемещение урону (cumulativeRawDamage), а не
  // отдельно на каждый шаг - иначе несколько мелких срабатываний по 1 уроку
  // округлялись бы в 0 потерь каждое по отдельности, вместо одного округления
  // суммы, как это уже сделано для нескольких атакующих в одной фазе боя
  const isOrc = isUnitFaction(movingUnit, FACTIONS.ORCS);
  let remainingSize = movingUnit.size;
  let cumulativeRawDamage = 0;
  const damageByGoblinPlayer = {};
  const trapCountByGoblinPlayer = {};
  // Группировка по СОБСТВЕННОМУ гексу ловушки (а не по гексу шага, на
  // котором отряд оказался смежен ей) - индикатор урона должен рисоваться
  // на том гексе, где стояла ловушка, чтобы игрок понимал, какую именно
  // ловушку он обезвредил (см. add-goblins-faction design.md Decision 22).
  // "Смежность" не означает "тот же гекс": ловушка может сработать, стоя на
  // СОСЕДНЕМ с шагом гексе, и на одном шаге может сработать сразу несколько
  // ловушек НА РАЗНЫХ гексах вокруг отряда
  const trapsByOwnerByTrapHex = new Map();
  const removedTrapIds = [];
  let unitDied = false;
  let finalStepIndex = lastIndex;

  for (const stepIndex of stepIndexesToCheck) {
    const stepHex = path[stepIndex];
    const stepHexKey = coordsToKey(stepHex.q, stepHex.r);
    const adjacentKeys = new Set([stepHexKey, ...getNeighborHexKeys(stepHexKey)]);

    // Ловушка, уже сработавшая на более раннем шаге ЭТОГО ЖЕ перемещения, не
    // может сработать снова, даже если её гекс снова окажется смежным на
    // следующем шаге (например, при движении "туда-обратно" мимо неё)
    const alreadyTriggeredIds = new Set(removedTrapIds);
    const trapsHere = traps.filter(trap =>
      adjacentKeys.has(trap.hexKey)
      && trap.ownerId !== movingUnit.ownerId
      && !alreadyTriggeredIds.has(trap.id)
    );

    if (trapsHere.length === 0) {
      continue;
    }

    let stepDamage = 0;

    for (const trap of trapsHere) {
      const damage = getGoblinsTrapDamageRoll();
      stepDamage += damage;
      damageByGoblinPlayer[trap.ownerId] = (damageByGoblinPlayer[trap.ownerId] || 0) + damage;
      trapCountByGoblinPlayer[trap.ownerId] = (trapCountByGoblinPlayer[trap.ownerId] || 0) + 1;
      removedTrapIds.push(trap.id);

      if (!trapsByOwnerByTrapHex.has(trap.hexKey)) {
        trapsByOwnerByTrapHex.set(trap.hexKey, []);
      }
      trapsByOwnerByTrapHex.get(trap.hexKey).push({ownerId: trap.ownerId, damage});
    }

    cumulativeRawDamage += stepDamage;
    const effectiveLoss = isOrc ? calculateOrcLoss(cumulativeRawDamage) : cumulativeRawDamage;
    remainingSize = movingUnit.size - effectiveLoss;

    if (remainingSize <= 0) {
      unitDied = true;
      finalStepIndex = stepIndex;
      break;
    }
  }

  const triggeredTrapEvents = [...trapsByOwnerByTrapHex.entries()]
    .map(([hexKey, trapsByOwner]) => ({hexKey, trapsByOwner}));

  const finalHex = path[finalStepIndex];
  const finalHexKey = coordsToKey(finalHex.q, finalHex.r);

  // Атрибуция убийства при нескольких игроках-гоблинах — см. design.md
  // Decision 6: суммарный урон -> число сработавших ловушек -> очередь хода
  let killerPlayerId = null;
  if (unitDied) {
    const candidates = Object.keys(damageByGoblinPlayer);
    if (candidates.length === 1) {
      killerPlayerId = candidates[0];
    } else if (candidates.length > 1) {
      const orderedTurn = getTurnOrderFromPlayer(movingUnit.ownerId, turnOrder || []);
      const turnRank = (playerId) => {
        const idx = orderedTurn.indexOf(playerId);
        return idx === -1 ? Number.MAX_SAFE_INTEGER : idx;
      };

      candidates.sort((a, b) => {
        if (damageByGoblinPlayer[b] !== damageByGoblinPlayer[a]) {
          return damageByGoblinPlayer[b] - damageByGoblinPlayer[a];
        }
        if (trapCountByGoblinPlayer[b] !== trapCountByGoblinPlayer[a]) {
          return trapCountByGoblinPlayer[b] - trapCountByGoblinPlayer[a];
        }
        return turnRank(a) - turnRank(b);
      });
      killerPlayerId = candidates[0];
    }
  }

  return {
    finalHexKey,
    finalStepIndex,
    unitDied,
    remainingSize: Math.max(remainingSize, 0),
    killerPlayerId,
    triggeredTrapEvents,
    removedTrapIds,
  };
};
